import { RecurringJournal } from '../../interfaces/recurringJournal.interface';
import { humanizeEnum } from '../../utils/format';

/** "Monthly on the 5th", "Every 2 weeks", "Quarterly on the last day"... */
export const describeSchedule = (t: Pick<RecurringJournal, 'frequency' | 'intervalCount' | 'dayOfMonth' | 'lastDayOfMonth'>): string => {
    const n = t.intervalCount || 1;
    const unit = { WEEKLY: 'week', MONTHLY: 'month', QUARTERLY: 'quarter', YEARLY: 'year' }[t.frequency];
    const every = n === 1 ? humanizeEnum(t.frequency) : `Every ${n} ${unit}s`;
    if (t.frequency === 'WEEKLY') return every;
    const day = t.lastDayOfMonth ? 'the last day' : `day ${t.dayOfMonth}`;
    return `${every} on ${day}`;
};

export const statusColor = (s: string): 'success' | 'warning' | 'default' | 'error' =>
    s === 'ACTIVE' || s === 'GENERATED' ? 'success'
        : s === 'PAUSED' || s === 'SKIPPED' ? 'warning'
            : s === 'FAILED' ? 'error' : 'default';

/**
 * How many occurrences fall on or before `today` starting at `nextRun` (the first not-yet-generated
 * date), capped by endDate / remaining count. Mirrors the server schedule; used for back-date warnings.
 */
export const countDueOccurrences = (
    t: { frequency: string; intervalCount: number; dayOfMonth?: number | null; lastDayOfMonth: boolean; endDate?: string | null; maxOccurrences?: number | null; occurrencesProcessed?: number },
    nextRun: string,
    today: string,
): { count: number; first?: string } => {
    const lim = (t.maxOccurrences ?? Infinity) - (t.occurrencesProcessed ?? 0);
    const monthsStep = t.frequency === 'MONTHLY' ? t.intervalCount : t.frequency === 'QUARTERLY' ? 3 * t.intervalCount : 12 * t.intervalCount;
    let [y, m, d] = nextRun.split('-').map(Number);
    const iso = (yy: number, mm: number, dd: number) =>
        `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    let cur = nextRun;
    let count = 0;
    while (cur <= today && (!t.endDate || cur <= t.endDate) && count < lim && count < 1000) {
        count++;
        if (t.frequency === 'WEEKLY') {
            const dt = new Date(Date.UTC(y, m - 1, d + 7 * t.intervalCount));
            y = dt.getUTCFullYear(); m = dt.getUTCMonth() + 1; d = dt.getUTCDate();
        } else {
            const total = y * 12 + (m - 1) + monthsStep;
            y = Math.floor(total / 12); m = (total % 12) + 1;
            d = t.lastDayOfMonth ? new Date(Date.UTC(y, m, 0)).getUTCDate() : (t.dayOfMonth ?? d);
        }
        cur = iso(y, m, d);
    }
    return { count, first: count > 0 ? nextRun : undefined };
};

/** First occurrence on or after the start date for a monthly-style rule (weekly = start date). */
export const firstOccurrence = (
    frequency: string, dayOfMonth: number | null, lastDay: boolean, start: string,
): string => {
    if (frequency === 'WEEKLY') return start;
    const [y, m, d] = start.split('-').map(Number);
    const day = lastDay ? new Date(Date.UTC(y, m, 0)).getUTCDate() : (dayOfMonth ?? d);
    const cand = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (cand >= start) return cand;
    const total = y * 12 + (m - 1) + 1;
    const ny = Math.floor(total / 12), nm = (total % 12) + 1;
    const nd = lastDay ? new Date(Date.UTC(ny, nm, 0)).getUTCDate() : (dayOfMonth ?? d);
    return `${ny}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
};
